import { getPercentFromPointerEvent } from "../../utils/pointer.js";
import { createState } from "@videojs/store";
import { observeResize } from "@videojs/utils/dom";
import { isNull } from "@videojs/utils/predicate";
import { clamp, roundToStep } from "@videojs/utils/number";
import { throttle } from "@videojs/utils/function";
//#region src/dom/ui/slider/slider.ts
const DRAG_THRESHOLD = 3;
function createSlider(options) {
	const input = createState({
		pointerPercent: 0,
		dragPercent: 0,
		dragging: false,
		pointing: false,
		focused: false
	});
	const abort = new AbortController();
	const changeThrottleMs = options.changeThrottle ?? 0;
	let isPointerDown = false, cachedRect = null, capturedPointerId = null, pointerDownX = 0, pointerDownY = 0, lastDragPercent = 0, lastKeyPercent = null, committedOnRelease = false, pointingOnRelease = false;
	const throttledChange = changeThrottleMs > 0 ? throttle((percent) => options.onValueChange?.(percent), changeThrottleMs, { leading: true }) : null;
	/** Fire `onValueChange` — throttled during drag when `changeThrottle > 0`. */
	function fireChange(percent, duringDrag) {
		if (duringDrag && throttledChange) throttledChange(percent);
		else options.onValueChange?.(percent);
	}
	function releaseCapture() {
		if (isNull(capturedPointerId)) return;
		const id = capturedPointerId;
		capturedPointerId = null;
		try {
			options.getElement().releasePointerCapture(id);
		} catch {}
	}
	function endDrag() {
		if (!isPointerDown) return;
		const pointing = committedOnRelease && pointingOnRelease;
		const wasDragging = input.current.dragging;
		if (!committedOnRelease) options.onValueCommit?.(lastDragPercent);
		isPointerDown = false;
		input.patch({
			dragging: false,
			pointing
		});
		if (wasDragging) options.onDragEnd?.();
		options.onPressEnd?.();
		committedOnRelease = false;
		pointingOnRelease = false;
		cleanup();
	}
	function cleanup() {
		throttledChange?.cancel();
		capturedPointerId = null;
		cachedRect = null;
	}
	const rootProps = {
		onPointerDown(event) {
			if (options.isDisabled()) return;
			event.stopPropagation();
			event.preventDefault();
			const el = options.getElement();
			cachedRect = el.getBoundingClientRect();
			committedOnRelease = false;
			pointingOnRelease = false;
			releaseCapture();
			capturedPointerId = event.pointerId;
			el.setPointerCapture(event.pointerId);
			const percent = getPercentFromPointerEvent(event, cachedRect, options.getOrientation());
			isPointerDown = true;
			pointerDownX = event.clientX;
			pointerDownY = event.clientY;
			lastDragPercent = percent;
			lastKeyPercent = percent;
			input.patch({
				pointing: true,
				pointerPercent: percent,
				dragPercent: percent
			});
			options.onPressStart?.();
			options.onValueChange?.(percent);
			options.getThumbElement?.()?.focus({
				preventScroll: true,
				focusVisible: false
			});
		},
		onPointerMove(event) {
			if (options.isDisabled()) return;
			if (!isNull(capturedPointerId)) {
				if (event.pointerType !== "touch" && event.buttons === 0) {
					endDrag();
					return;
				}
				const percent = getPercentFromPointerEvent(event, cachedRect, options.getOrientation());
				const startingDrag = !input.current.dragging;
				if (startingDrag) {
					if (Math.hypot(event.clientX - pointerDownX, event.clientY - pointerDownY) < DRAG_THRESHOLD) return;
				}
				lastDragPercent = percent;
				lastKeyPercent = percent;
				input.patch({
					dragging: true,
					dragPercent: percent,
					pointerPercent: percent
				});
				if (startingDrag) options.onDragStart?.();
				fireChange(percent, true);
				return;
			}
			const rect = options.getElement().getBoundingClientRect();
			const percent = getPercentFromPointerEvent(event, rect, options.getOrientation());
			input.patch({
				pointing: true,
				pointerPercent: percent
			});
		},
		onPointerUp(event) {
			if (options.isDisabled()) return;
			event.stopPropagation();
			if (isNull(capturedPointerId)) return;
			const percent = getPercentFromPointerEvent(event, cachedRect, options.getOrientation());
			const releaseRect = options.getElement().getBoundingClientRect();
			pointingOnRelease = event.pointerType !== "touch" && event.clientX >= releaseRect.left && event.clientX <= releaseRect.right && event.clientY >= releaseRect.top && event.clientY <= releaseRect.bottom;
			throttledChange?.cancel();
			options.onValueChange?.(percent);
			options.onValueCommit?.(percent);
			committedOnRelease = true;
		},
		onPointerLeave() {
			if (!isNull(capturedPointerId)) return;
			input.patch({ pointing: false });
		},
		onLostPointerCapture() {
			endDrag();
		}
	};
	const thumbProps = {
		onKeyDownCapture(event) {
			if (options.isDisabled()) {
				if (event.key !== "Tab") event.preventDefault();
				return;
			}
			const stepPercent = options.getStepPercent();
			const largeStepPercent = options.getLargeStepPercent();
			const currentPercent = event.repeat && !isNull(lastKeyPercent) ? lastKeyPercent : options.getPercent();
			const rounded = roundToStep(currentPercent, stepPercent, 0);
			const step = event.shiftKey ? largeStepPercent : stepPercent;
			let newPercent = null;
			switch (event.key) {
				case "ArrowRight":
					newPercent = rounded + step;
					break;
				case "ArrowLeft":
					newPercent = rounded - step;
					break;
				case "ArrowUp":
					newPercent = rounded + step;
					break;
				case "ArrowDown":
					newPercent = rounded - step;
					break;
				case "PageUp":
					newPercent = rounded + largeStepPercent;
					break;
				case "PageDown":
					newPercent = rounded - largeStepPercent;
					break;
				case "Home":
					newPercent = 0;
					break;
				case "End": newPercent = 100;
			}
			if (newPercent !== null) {
				event.preventDefault();
				newPercent = clamp(newPercent, 0, 100);
				lastKeyPercent = newPercent;
				input.patch({
					pointerPercent: newPercent,
					dragPercent: newPercent,
					pointing: false
				});
				options.onValueChange?.(newPercent);
				options.onValueCommit?.(newPercent);
			}
		},
		onFocus() {
			input.patch({ focused: true });
		},
		onBlur() {
			input.patch({ focused: false });
		}
	};
	function adjustForAlignment(state) {
		if (!options.adjustPercent || state.thumbAlignment !== "edge") return state;
		const rootEl = options.getElement();
		const thumbEl = options.getThumbElement?.();
		if (!thumbEl) return state;
		const isHorizontal = state.orientation === "horizontal";
		const thumbSize = isHorizontal ? thumbEl.offsetWidth : thumbEl.offsetHeight;
		const trackSize = isHorizontal ? rootEl.offsetWidth : rootEl.offsetHeight;
		return {
			...state,
			fillPercent: options.adjustPercent(state.fillPercent, thumbSize, trackSize),
			pointerPercent: options.adjustPercent(state.pointerPercent, thumbSize, trackSize)
		};
	}
	let stopObservingResize = null;
	if (options.onResize) stopObservingResize = observeResize(options.getElement(), () => options.onResize());
	return {
		input,
		rootProps,
		rootStyle: {
			touchAction: "none",
			userSelect: "none"
		},
		thumbProps,
		adjustForAlignment,
		destroy() {
			if (abort.signal.aborted) return;
			abort.abort();
			stopObservingResize?.();
			releaseCapture();
			cleanup();
		}
	};
}
//#endregion
export { createSlider };

//# sourceMappingURL=slider.js.map